import { useState, useEffect, useRef, useCallback } from "react";
import { X, Send, Sparkles, ExternalLink, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REGISTER_URL = "https://portal.gdprlocal.com/Account/Register";

const SEV_STYLE = {
    high: "bg-[#fdeaea] text-danger",
    medium: "bg-[#fff5e0] text-amber",
    low: "bg-asphalt-tint text-asphalt",
};

// Contextual tap-to-ask suggestions based on the gap's mapped service.
const quickReplies = (gap) => {
    const s = (gap?.service || "").toLowerCase();
    const specific = [];
    if (s.includes("representative")) specific.push("What does an Article 27 representative do?");
    else if (s.includes("dpo")) specific.push("Do we actually need a DPO?");
    else if (s.includes("ai governance")) specific.push("What does AI governance involve?");
    else specific.push("How does GDPRLocal fix this?");
    return [...specific, "What does the free account include?", "How long does setup take?", "How do I get started?"].slice(0, 4);
};

export const ChatPanel = ({ gap, company, overallGrade, onClose }) => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [asked, setAsked] = useState([]);
    const sessionId = useRef(`chat-${Date.now()}`);
    const scrollRef = useRef(null);
    const startedRef = useRef(false);

    const scrollToBottom = () => {
        requestAnimationFrame(() => {
            if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        });
    };

    const send = useCallback(async (text) => {
        setStreaming(true);
        const history = messages.map((m) => ({ role: m.role, content: m.content }));
        const base = Date.now();
        if (text) setMessages((p) => [...p, { id: `u-${base}`, role: "user", content: text }]);
        setMessages((p) => [...p, { id: `a-${base}`, role: "assistant", content: "" }]);
        scrollToBottom();
        try {
            const resp = await fetch(`${API}/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    session_id: sessionId.current,
                    message: text || "",
                    history,
                    gap,
                    company,
                    overall_grade: overallGrade,
                }),
            });
            const reader = resp.body.getReader();
            const dec = new TextDecoder();
            let buffer = "";
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                buffer += dec.decode(value, { stream: true });
                const parts = buffer.split("\n\n");
                buffer = parts.pop();
                for (const p of parts) {
                    const line = p.trim();
                    if (!line.startsWith("data:")) continue;
                    const data = line.slice(5).trim();
                    if (data === "[DONE]") continue;
                    try {
                        const j = JSON.parse(data);
                        if (j.delta) {
                            setMessages((prev) => {
                                const copy = [...prev];
                                const last = copy[copy.length - 1];
                                copy[copy.length - 1] = { ...last, role: "assistant", content: last.content + j.delta };
                                return copy;
                            });
                            scrollToBottom();
                        } else if (j.error) {
                            setMessages((prev) => {
                                const copy = [...prev];
                                const last = copy[copy.length - 1];
                                copy[copy.length - 1] = { ...last, role: "assistant", content: j.error };
                                return copy;
                            });
                        }
                    } catch (err) { console.debug("chat: skipping unparsable SSE chunk", err); }
                }
            }
        } catch (err) {
            console.warn("chat stream failed", err);
            setMessages((prev) => {
                const copy = [...prev];
                const last = copy[copy.length - 1];
                copy[copy.length - 1] = { ...last, role: "assistant", content: "The assistant could not be reached. Please try again." };
                return copy;
            });
        } finally {
            setStreaming(false);
            scrollToBottom();
        }
    }, [messages, gap, company, overallGrade]);

    // opening message when a gap is selected
    useEffect(() => {
        setMessages([]);
        setAsked([]);
        startedRef.current = false;
        sessionId.current = `chat-${Date.now()}`;
        const t = setTimeout(() => {
            if (!startedRef.current) {
                startedRef.current = true;
                send("");
            }
        }, 60);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gap?.issue]);

    const onSubmit = (e) => {
        e.preventDefault();
        const t = input.trim();
        if (!t || streaming) return;
        setInput("");
        send(t);
    };

    const askQuick = (q) => {
        if (streaming) return;
        setAsked((p) => [...p, q]);
        send(q);
    };

    const suggestions = quickReplies(gap).filter((q) => !asked.includes(q));

    return (
        <>
            <div className="fixed inset-0 bg-asphalt/30 z-40 md:bg-transparent md:pointer-events-none" onClick={onClose} data-testid="chat-backdrop" />
            <aside
                className="fixed top-0 right-0 h-full z-50 bg-white border-l border-line shadow-2xl flex flex-col w-full sm:w-[380px] lg:w-[24vw] lg:min-w-[360px] gdpr-fade-up"
                data-testid="chat-panel"
                style={{ animationDuration: "0.3s" }}
            >
                <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-line bg-asphalt text-white">
                    <div className="flex items-start gap-2">
                        <Sparkles className="w-5 h-5 text-aqua mt-0.5 shrink-0" />
                        <div>
                            <p className="text-sm font-bold leading-tight">GDPRLocal assistant</p>
                            {gap && <span className={`inline-block mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full uppercase ${SEV_STYLE[gap.severity] || "bg-white/20"}`}>{gap.severity}</span>}
                        </div>
                    </div>
                    <button onClick={onClose} data-testid="chat-close" className="text-white/70 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
                </div>

                {gap && (
                    <div className="px-5 py-3 border-b border-line bg-asphalt-tint">
                        <p className="text-xs text-muted leading-snug">{gap.issue}</p>
                    </div>
                )}

                <div ref={scrollRef} className="flex-1 overflow-y-auto gdpr-scroll px-5 py-4 space-y-4">
                    {messages.map((m, i) => (
                        <div key={m.id || i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-aqua text-asphalt" : "bg-asphalt-tint text-asphalt"}`}
                                data-testid={`chat-msg-${m.role}`}>
                                {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="w-4 h-4 gdpr-spin text-muted" /> : "")}
                            </div>
                        </div>
                    ))}
                </div>

                {suggestions.length > 0 && messages.length > 0 && (
                    <div className="px-5 pt-1 pb-2 flex flex-wrap gap-2" data-testid="quick-replies">
                        {suggestions.map((q, i) => (
                            <button key={q} onClick={() => askQuick(q)} disabled={streaming}
                                data-testid={`quick-reply-${i}`}
                                className="text-xs font-medium px-3 py-1.5 rounded-full border border-aqua/60 text-asphalt bg-aqua-tint hover:bg-aqua/30 disabled:opacity-40 transition-colors">
                                {q}
                            </button>
                        ))}
                    </div>
                )}

                <div className="px-5 pt-2">
                    <a href={REGISTER_URL} target="_blank" rel="noreferrer" data-testid="chat-register-cta"
                        className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md bg-aqua text-asphalt font-medium text-sm hover:brightness-95 transition-all">
                        Create a free account <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                </div>

                <form onSubmit={onSubmit} className="p-4 flex items-center gap-2 border-t border-line mt-2">
                    <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="chat-input"
                        placeholder="Ask how GDPRLocal can help..."
                        className="flex-1 px-3 py-2.5 border border-line rounded-md outline-none focus:border-aqua text-sm" />
                    <button type="submit" disabled={streaming || !input.trim()} data-testid="chat-send"
                        className="p-2.5 rounded-md bg-asphalt text-white disabled:opacity-40 hover:brightness-110 transition-all">
                        <Send className="w-4 h-4" />
                    </button>
                </form>
            </aside>
        </>
    );
};
